<template>
  <v-dialog v-model="computedValue" width="700">
    <v-card>
      <v-card-title>Download Options</v-card-title>
      <v-card-text>
        <InputGeneric label="Resources" :label-size="3" class="mb-3">
          <v-autocomplete v-model="resources" hide-details :items="resourcesItems" dense multiple>
            <template #prepend-item>
              <v-list-item>
                <v-list-item-action>
                  <v-icon :color="resources.length > 0 ? 'primary' : ''">
                    {{ icon(resourcesItems, resources) }}
                  </v-icon>
                </v-list-item-action>
                <v-list-item-content>
                  <v-list-item-title> Select All </v-list-item-title>
                </v-list-item-content>
              </v-list-item>
              <v-divider></v-divider>
            </template>
          </v-autocomplete>
        </InputGeneric>
        <InputGeneric label="Nodes" :label-size="3" class="mb-3">
          <v-autocomplete v-model="nodes" hide-details :items="nodesItems" dense multiple>
            <template #prepend-item>
              <v-list-item dense>
                <v-list-item-action>
                  <v-icon :color="nodes.length > 0 ? 'primary' : ''">
                    {{ icon(nodesItems, nodes) }}
                  </v-icon>
                </v-list-item-action>
                <v-list-item-content>
                  <v-list-item-title> Select All </v-list-item-title>
                </v-list-item-content>
              </v-list-item>
              <v-divider></v-divider>
            </template>
          </v-autocomplete>
        </InputGeneric>
        <InputGeneric label="Years" :label-size="3" class="mb-3">
          <v-autocomplete v-model="years" hide-details :items="yearsItems" dense multiple>
            <template #prepend-item>
              <v-list-item @mousedown.prevent @click="toggle('years')">
                <v-list-item-action>
                  <v-icon :color="years.length > 0 ? 'primary' : ''">
                    {{ icon(yearsItems, years) }}
                  </v-icon>
                </v-list-item-action>
                <v-list-item-content>
                  <v-list-item-title> Select All </v-list-item-title>
                </v-list-item-content>
              </v-list-item>
              <v-divider></v-divider>
            </template>
          </v-autocomplete>
        </InputGeneric>
      </v-card-text>
      <v-card-actions>
        <v-spacer> </v-spacer>
        <v-btn
          small
          class="ma-1"
          outlined
          color="primary"
          @click="downloadOutputs"
          :loading="downloadPending"
        >
          <v-icon small>mdi-download</v-icon>
          download
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { saveAs } from "file-saver";
import Zip from "jszip";
import { DateTime } from "luxon";
import { utils, write } from "xlsx";
import { sqlToObject } from "@/mixins/sql-utils";
import { camelToTitleCase, snakeToTitleCase } from "@/plugins/utils";
import initSqlDb from "@/utils/duckdb-client";

export default {
  name: "NPCTimeseriesDownload",
  components: {
    InputGeneric: () => import("@/components/inputs/InputGeneric.vue"),
  },
  mixins: [],
  props: {
    currentCase: { type: Object, required: true },
    value: { type: Boolean, required: true },
    parquetFiles: { type: Object }, // SAS URLs for each Parquet file
    yearlyOutput: { type: Object },
  },
  data() {
    return {
      resources: [],
      resourcesItems: [
        { value: "demands", text: "Demands" },
        { value: "hydro_generators", text: "Hydro Generators" },
        { value: "markets", text: "Markets" },
        { value: "nodes", text: "Nodes" },
        { value: "renewable_generators", text: "Renewable Generators" },
        { value: "storages", text: "Storages" },
        { value: "thermal_generators", text: "Thermal Generators" },
      ],
      nodes: [],
      nodesItems: [],
      years: [],
      yearsItems: [],
      db: null,
      downloadPending: false,
    };
  },
  computed: {
    computedValue: {
      get() {
        return this.value;
      },
      set(v) {
        this.$emit("input", v);
      },
    },
  },
  watch: {
    yearlyOutput() {
      this.populateItemsLists();
    },
  },
  beforeCreate() {},
  created() {
    if (this.yearlyOutput) {
      this.populateItemsLists();
    }
  },
  beforeMount() {},
  mounted() {},
  beforeUpdate() {},
  updated() {},
  activated() {},
  deactivated() {},
  beforeDestroy() {},
  destroyed() {},
  methods: {
    populateItemsLists() {
      this.yearsItems = Object.keys(this.yearlyOutput.yearlyResults);
      this.nodesItems = Object.values(Object.values(this.yearlyOutput.yearlyResults)[0].nodes).map(
        (node) => ({ value: node.id, text: `${node.name} (ID: ${node.id})` })
      );
    },
    icon(items, value) {
      if (items.length === value.length) return "mdi-close-box";
      if (items.length > value.length && value.length > 0) return "mdi-minus-box";

      return "mdi-checkbox-blank-outline";
    },
    toggle(propName) {
      this.$nextTick(() => {
        if (this[`${propName}Items`].length === this[propName].length) {
          this[propName] = [];
        } else {
          this[propName] = this[`${propName}Items`].slice();
        }
      });
    },
    async resourceYearlyWorkbook(tableName) {
      const workbooks = [];
      const years = this.years.map((v) => Number(v));
      const columnsResponse = await this.db.exec(
        `SELECT name FROM pragma_table_info('${tableName}')`
      );
      const properties = columnsResponse[0]
        ? columnsResponse[0].values
            .map((row) => row[0])
            .filter(
              (name) =>
                !["projectID", "name", "datetime", "nodeID", "branchID", "reserves"].includes(name)
            )
        : [];
      const namesRes = await this.db.exec(
        `SELECT DISTINCT name from '${tableName}' WHERE nodeID IN (${this.nodes.join()})`
      );
      const names = namesRes[0] ? namesRes[0].values : [];
      if (names.length) {
        const promises = years.flatMap((year) => {
          return properties.map(async (col) => {
            const workbook = utils.book_new();
            const namesSQL = names
              .map((name) => `SUM(${col}) FILTER (WHERE name = '${name}') AS '${name}'`)
              .join();
            const startDate = DateTime.fromISO(`${year}-01-01T00:00:00.000Z`);
            const endDate = DateTime.fromISO(`${Number(year) + 1}-01-01T00:00:00.000Z`);
            const response = await this.db.exec(
              `SELECT datetime,
              ${namesSQL}
              FROM '${tableName}'
              WHERE datetime >= ${startDate.toUnixInteger()}
              AND datetime < '${endDate.toUnixInteger()}'
              AND nodeID IN (${this.nodes.join()})
              GROUP BY datetime`
            );
            if (response[0]) {
              const objectKeys = response[0].columns.map((colName) => camelToTitleCase(colName));
              const jsonArray = response[0].values
                .map((row) => sqlToObject(objectKeys, row))
                .map((row) => ({
                  ...row,
                  Datetime: DateTime.fromISO(row.Datetime)
                    .setZone("utc")
                    .toSQL({ includeOffset: false }),
                }));
              if (jsonArray.length) {
                const opts = {
                  header: Object.keys(jsonArray[0]),
                };
                const worksheet = utils.json_to_sheet(jsonArray, opts);
                utils.book_append_sheet(workbook, worksheet, camelToTitleCase(col));
              }
              workbooks.push({
                workbook,
                name: `${snakeToTitleCase(tableName)}_${camelToTitleCase(col)}_${year}.csv`,
              });
            }
          });
        });
        await Promise.all(promises);
      }

      return workbooks;
    },
    async yearlyResults() {
      const workbooks = await Promise.all(
        this.resources.map((name) => {
          return this.resourceYearlyWorkbook(name);
        })
      );
      return workbooks.flat();
    },
    async downloadOutputs() {
      this.downloadPending = true;
      try {
        this.db = await initSqlDb({ parquet_files: this.parquetFiles });

        const yearlyResults = await this.yearlyResults(this.db);

        await this.db.close();
        this.db = null;

        const zip = new Zip();
        yearlyResults.forEach((wb) => {
          const wbout = write(wb.workbook, {
            bookSST: true,
            type: "binary",
          });
          zip.file(wb.name, wbout, { binary: true });
        });

        zip.generateAsync({ type: "blob" }).then((content) => {
          // see FileSaver.js
          saveAs(content, `${this.currentCase.name}_timeseries_NPC.zip`);
        });
      } catch (error) {
        console.error("Error downloading NPC timeseries:", error);
      } finally {
        this.downloadPending = false;
      }
    },
  },
};
</script>

<style></style>
